/**
 * Configuration SpinTouch du poste : quel disque de reactif est utilise par
 * defaut, et quel type d'echantillon proposer pour ce disque.
 *
 * Le reglage est local au poste (localStorage) : chaque magasin gere son
 * propre stock de disques, sans dependance au backend.
 *
 * La doc WaterLink Connect v2 (1.7) demande explicitement de ne pas figer les
 * valeurs renvoyees par /options : "It is best to avoid hard coding Sample Type
 * and Disk Type options as these are subject to change at LaMotte's discretion".
 * D'ou la resolution par nom avec repli sur la valeur, et la recherche des
 * echantillons par Tag plutot que par Value.
 */

export const CLE_DISQUE = 'SPINTOUCH_DISQUE_DEFAUT';

// Seul disque commercialise par LaMotte pour nos analyses : il couvre le
// chlore, le brome et le sel a lui seul.
export const DISQUE_USINE = {
  name: '203 Series Cl/Br With Phosphate Disk',
  value: 4
};

// Les noms d'echantillons renvoyes par le service sont en anglais.
export const LIBELLES_ECHANTILLON = {
  chlorine: 'Chlore',
  bromine: 'Brome',
  salt: 'Sel',
  biguanide: 'Biguanide'
};

// Type de desinfection coche dans le formulaire -> tag d'echantillon WaterLink.
const TAG_PAR_DESINFECTION = [
  ['IsTypeDesinfection_Brome', 'bromine'],
  ['IsTypeDesinfection_Eletrolyseur', 'salt'],
  ['IsTypeDesinfection_ChloreGalet', 'chlorine'],
  ['IsTypeDesinfection_ChloreLiquide', 'chlorine']
];

const TAG_PAR_DEFAUT = 'chlorine';

/**
 * Disque configure sur ce poste. Retourne toujours un disque exploitable :
 * a defaut de reglage lisible, le disque usine.
 */
export function lireDisqueDefaut() {
  try {
    const brut = window.localStorage.getItem(CLE_DISQUE);
    if (!brut) {
      return DISQUE_USINE;
    }
    const disque = JSON.parse(brut);
    if (typeof disque.name === 'string' && typeof disque.value === 'number') {
      return { name: disque.name, value: disque.value };
    }
  } catch (error) {
    // localStorage indisponible ou contenu corrompu : on repart du disque usine.
  }
  return DISQUE_USINE;
}

/**
 * Memorise le disque choisi dans la page de configuration.
 * @param {{Name: string, Value: number}} reagent - entree de /options
 */
export function ecrireDisqueDefaut(reagent) {
  const disque = { name: reagent.Name, value: reagent.Value };
  try {
    window.localStorage.setItem(CLE_DISQUE, JSON.stringify(disque));
  } catch (error) {
    console.warn('SpinTouch : impossible de memoriser le disque par defaut', error);
  }
  return disque;
}

/**
 * Retrouve le disque configure dans la liste renvoyee par l'appareil.
 * @returns l'entree de reagentTypes, ou null si l'appareil ne la propose pas.
 */
export function resoudreDisque(reagentTypes, disque = lireDisqueDefaut()) {
  if (!Array.isArray(reagentTypes)) {
    return null;
  }
  return reagentTypes.find(r => r && r.Name === disque.name)
    || reagentTypes.find(r => r && r.Value === disque.value)
    || null;
}

/**
 * Echantillons utilisables avec ce disque, libelles en francais.
 * Un echantillon et un disque sont compatibles s'ils partagent au moins un tag.
 */
export function echantillonsCompatibles(sampleTypes, disque) {
  if (!Array.isArray(sampleTypes) || !disque || !Array.isArray(disque.Tags)) {
    return [];
  }
  return sampleTypes.reduce((compatibles, sample) => {
    const tag = Array.isArray(sample.Tags)
      ? sample.Tags.find(t => disque.Tags.includes(t))
      : undefined;
    if (tag) {
      compatibles.push({
        value: sample.Value,
        libelle: LIBELLES_ECHANTILLON[tag] || sample.Name,
        tag
      });
    }
    return compatibles;
  }, []);
}

/**
 * Echantillon a preselectionner dans le formulaire, deduit du type de
 * desinfection coche. Le piscinier reste libre d'en choisir un autre.
 */
export function echantillonParDefaut(compatibles, etatDesinfection = {}) {
  const entree = TAG_PAR_DESINFECTION.find(([champ]) => etatDesinfection[champ] === true);
  const tagVoulu = entree ? entree[1] : TAG_PAR_DEFAUT;

  return compatibles.find(e => e.tag === tagVoulu)
    || compatibles.find(e => e.tag === TAG_PAR_DEFAUT)
    || compatibles[0]
    || null;
}
